import { useCallback } from "react"
import { v4 as uuidv4 } from "uuid"
import {
  BadgeElement,
  BadgeElementType,
  BadgeTemplate,
} from "sharedTypes/badge"
import { getTemplateDimensions, updateTemplateElements } from "../utils/badgeHelpers"
import { getElementType } from "../elementTypes"

/**
 * Manages element CRUD operations
 */
const useElementOperations = (
  currentTemplate: BadgeTemplate | undefined,
  pushState: (template: BadgeTemplate) => void,
  setSelectedElementIds: (ids: string[]) => void,
  setInlineEditingElementId: (id: string | undefined) => void,
) => {
  const handleAddElement = useCallback((
    elementType: BadgeElementType,
    x: number,
    y: number,
  ) => {
    if (!currentTemplate) return

    const nextZIndex = currentTemplate.badgeData.elements.length > 0
      ? Math.max(...currentTemplate.badgeData.elements.map((el) => el.zIndex)) + 1
      : 1

    const definition = getElementType(elementType)
    if (!definition) return

    const newElement = {
      id: uuidv4(),
      type: elementType,
      x,
      y,
      width: definition.size.width,
      height: definition.size.height,
      zIndex: nextZIndex,
      data: definition.createData(),
      fill: definition.transparentFill ? "transparent" : undefined,
    } as BadgeElement

    pushState(updateTemplateElements(currentTemplate, (
      elements,
    ) => [...elements, newElement]))
    setSelectedElementIds([newElement.id])

    if (elementType === BadgeElementType.TEXT) {
      setInlineEditingElementId(newElement.id)
    }
  }, [currentTemplate, pushState, setSelectedElementIds, setInlineEditingElementId])

  const handleDeleteElement = useCallback((elementIds: string[]) => {
    if (!currentTemplate || elementIds.length === 0) return

    pushState(updateTemplateElements(currentTemplate, (
      elements,
    ) => elements.filter((
      element,
    ) => !elementIds.includes(element.id))))
    setSelectedElementIds([])
  }, [currentTemplate, pushState, setSelectedElementIds])

  const handleDuplicateElement = useCallback((elementIds: string[]) => {
    if (!currentTemplate || elementIds.length === 0) return

    const elementsToDuplicate = currentTemplate.badgeData.elements.filter(
      (el) => elementIds.includes(el.id),
    )
    if (elementsToDuplicate.length === 0) return

    const { width: templateWidth, height: templateHeight } = getTemplateDimensions(currentTemplate)

    const maxZIndex = Math.max(
      ...currentTemplate.badgeData.elements.map((el) => el.zIndex),
      0,
    )

    const duplicatedElements: BadgeElement[] = elementsToDuplicate.map(
      (elementToDuplicate, index) => ({
        ...elementToDuplicate,
        id: uuidv4(),
        x: Math.min(
          elementToDuplicate.x + 5,
          templateWidth - elementToDuplicate.width,
        ),
        y: Math.min(
          elementToDuplicate.y + 5,
          templateHeight - elementToDuplicate.height,
        ),
        zIndex: maxZIndex + 1 + index,
      }),
    )

    pushState(updateTemplateElements(currentTemplate, (
      elements,
    ) => [...elements, ...duplicatedElements]))
    setSelectedElementIds(duplicatedElements.map((el) => el.id))
  }, [currentTemplate, pushState, setSelectedElementIds])

  return {
    handleAddElement,
    handleDeleteElement,
    handleDuplicateElement,
  }
}

export default useElementOperations
